/**
 * Shared seam for the registration screens → Reset Internet Password (SCRN-57)
 * round-trip (issue #653, @dylan219; extended to ALL five registration screens
 * by #729 — Open24 / Business24 / VISA / Mortgage / Broker24).
 *
 * Once all registration security questions (+ 2FA where the screen has one)
 * pass, the
 * agent may navigate to the Reset Internet Password page and reset the IPW —
 * completion of the registration questions is treated as sufficient security
 * for THIS navigation (no extra 2FA gate), exactly mirroring the existing
 * broker-security → IPW option (#568). The IPW page, when reached FROM
 * registration, shows a Back button that returns to the ORIGINATING
 * registration screen with every answer + the Pass (the server-side `medium`
 * verification) intact — no re-answering, no re-doing 2FA — and offers NO
 * navigation to any other screen.
 *
 * The registration answer marks live in component state, which is lost when the
 * screen unmounts on navigation. To honour "all previous answers preserved on
 * Back", the registration screen snapshots its answer state into `sessionStorage`
 * (keyed by screen + CIF) before navigating, and restores it on mount. The
 * server-side `medium` verification (the "Pass") persists in the session across
 * the round-trip independently, so 2FA is never re-done.
 *
 * All five registration screens share this mechanism — they use the identical
 * helper (no screen-specific behaviour).
 */

/** The Reset Internet Password route (SCRN-57). */
export const RESET_INTERNET_PASSWORD_ROUTE = '/reset-internet-password';

/**
 * Router-state shape carried on the navigation from a registration screen to
 * the Reset Internet Password page. Its presence is what puts SCRN-57 into the
 * scoped "came from registration" mode: it renders a Back button (returning to
 * `from`) and hides every other navigation affordance.
 */
export interface ResetInternetPasswordLocationState {
  /** The originating registration route to return to on Back (e.g.
   * `/registration/business24`). */
  readonly from: string;
  /** The originating registration screen's RBAC code (SCRN-17..21),
   * carried for the audit trail / debugging. */
  readonly fromScreenCode?: string;
}

/** Narrow an unknown router-state value to a registration-origin state. Returns
 * `undefined` when the page was NOT reached from registration (the normal
 * broker-security → IPW flow, or a direct visit), so the caller falls back to
 * the existing "Done → ID&V" behaviour untouched. */
export function readRegistrationOrigin(
  state: unknown,
): ResetInternetPasswordLocationState | undefined {
  if (
    state !== null &&
    typeof state === 'object' &&
    'from' in state &&
    typeof (state as { from?: unknown }).from === 'string' &&
    (state as { from: string }).from.length > 0
  ) {
    const s = state as { from: string; fromScreenCode?: unknown };
    return {
      from: s.from,
      fromScreenCode:
        typeof s.fromScreenCode === 'string' ? s.fromScreenCode : undefined,
    };
  }
  return undefined;
}

/**
 * The subset of a registration screen's answer state that must survive the
 * round-trip to the IPW page and back. Purely Pass/Fail marks, question
 * selections, and completion toggles — NO on-file customer data (that is
 * re-fetched from `/security/questions` and lives in the server-side session),
 * so nothing sensitive is written to `sessionStorage`.
 */
export interface RegistrationAnswerSnapshot {
  readonly mandatoryStatus: Record<string, 'pass' | 'fail' | null>;
  readonly additionalStatus: Record<string, 'pass' | 'fail' | null>;
  readonly additionalDropdownChoice: Record<string, string>;
  readonly selectedAdditionalKey: string | null;
  readonly selectedAdditionalKeys: readonly string[];
  readonly otpStatus: 'pass' | 'fail' | null;
  readonly scriptingComplete: boolean;
  readonly maidenNotOnFile: boolean;
  readonly mobileNotOnFile: boolean;
  readonly skip2faRecorded: boolean;
  readonly skip2faCompleted: boolean;
  /**
   * The ECL "reason for non-completion" (skip-2FA) dropdown selection (issue
   * #790, @dylan219: "This reason should be retained when an agent navigates
   * away from the screen for IPW for example and returns to the registration
   * screen"). Persisted alongside the other answer marks so the selected
   * reason repopulates on the Back round-trip — for the `2FA Exemption Approved
   * by TL` reason it is selected but not yet recorded (`skip2faRecorded`
   * false), so without persisting the raw selection the reason would blank out
   * on return. Empty string when no reason is selected.
   */
  readonly skip2faReason: string;
  /**
   * The approving team lead's name entered for the `2FA Exemption Approved by
   * TL` skip reason (issue #790, #586 extension): retained across the IPW
   * round-trip together with {@link skip2faReason} so a mid-entry TL exemption
   * is not lost. Empty string for every other reason (and cleared server-side
   * on a non-TL reason).
   */
  readonly teamLeadName: string;
}

/** Shared key prefix for every registration answer snapshot, so Clear Session
 * can wipe them all (issue #790) without knowing the screen/CIF suffixes. */
const SNAPSHOT_KEY_PREFIX = 'ptsb.registration-answers.';

/** sessionStorage key for a registration screen's preserved answers, scoped to
 * the screen code AND the identified customer so a snapshot can never restore
 * onto a different screen or a different customer. */
function snapshotKey(screenCode: string, cif: string): string {
  return `${SNAPSHOT_KEY_PREFIX}${screenCode}.${cif}`;
}

/** Persist a registration screen's answer snapshot before navigating to the IPW
 * page, so Back can restore every answer. Best-effort: a storage failure never
 * blocks the navigation (the reset itself does not depend on it). */
export function saveRegistrationAnswers(
  screenCode: string,
  cif: string,
  snapshot: RegistrationAnswerSnapshot,
): void {
  if (!screenCode || !cif) return;
  try {
    sessionStorage.setItem(
      snapshotKey(screenCode, cif),
      JSON.stringify(snapshot),
    );
  } catch {
    // Storage unavailable / quota — the round-trip still works, answers just
    // won't repopulate. Never throw into the navigation path.
  }
}

/** Read a registration screen's answer snapshot WITHOUT consuming it. Returns
 * `undefined` when there is nothing to restore. Safe to call during render (it
 * has no side effect) — the caller consumes the key separately via
 * {@link clearRegistrationAnswers} in an effect, so a React StrictMode
 * double-render does not lose the snapshot. */
export function peekRegistrationAnswers(
  screenCode: string,
  cif: string,
): RegistrationAnswerSnapshot | undefined {
  if (!screenCode || !cif) return undefined;
  try {
    const raw = sessionStorage.getItem(snapshotKey(screenCode, cif));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as RegistrationAnswerSnapshot;
    // Minimal shape guard — a malformed value restores nothing.
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.scriptingComplete === 'boolean'
    ) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Consume (remove) a registration screen's answer snapshot so it only ever
 * restores ONCE — a subsequent visit / a fresh customer starts clean. Called
 * from an effect (never during render), so it is StrictMode-safe. */
export function clearRegistrationAnswers(
  screenCode: string,
  cif: string,
): void {
  if (!screenCode || !cif) return;
  try {
    sessionStorage.removeItem(snapshotKey(screenCode, cif));
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * Removes EVERY persisted registration answer snapshot (all screens / all
 * customers). Called by App's Clear Session (CCF-10, mirroring the security-run
 * snapshot wipe of #780/#778): a clear-down re-authenticates the customer from
 * scratch, so any in-progress registration answers — including the retained
 * "reason for non-completion" of issue #790 — must NOT survive it. Retention is
 * scoped to navigation within the same session; a clear-session is a hard reset.
 * Best-effort: a storage failure is swallowed.
 */
export function clearAllRegistrationAnswers(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(SNAPSHOT_KEY_PREFIX)) toRemove.push(key);
    }
    for (const key of toRemove) sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/** Restore (and consume) a registration screen's answer snapshot in one call.
 * Convenience for non-render callers (e.g. tests); the screen itself uses
 * {@link peekRegistrationAnswers} at render + {@link clearRegistrationAnswers}
 * in an effect to stay StrictMode-safe. */
export function takeRegistrationAnswers(
  screenCode: string,
  cif: string,
): RegistrationAnswerSnapshot | undefined {
  const snap = peekRegistrationAnswers(screenCode, cif);
  if (snap) clearRegistrationAnswers(screenCode, cif);
  return snap;
}
